import { getByID } from '@adornis/baseql/operations/mongo.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { countingTableView } from './record-count-view.js';
import { guardDialog } from '@adornis/dialog/dialog-functions.js';
import { ErmValue } from '@adornis/entity-relationship-management/client/details/erm-value.js';
import type { ERMEntityDefinition } from '@adornis/entity-relationship-management/client/types.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { iso639, Language } from '@adornis/internationalization/language-iso639-2.js';
import { html, nothing, type TemplateResult } from 'lit';
import { softDeleteCourse } from '../api/lms-queries.js';
import { countScopedCourses, subscribeScopedCoursesPaginated } from '../api/lms-scoped-queries.js';
import { LMSCourse } from '../entities/LMSCourse.js';
import { CourseEnrollmentManager } from './course-enrollment-manager.js';
import { CoursePageEditorSection } from './course-page-editor.js';

export interface CourseManagerConfigOpts {
  enrollmentManagerComponent?: (props: { courseId: string }) => TemplateResult;
}

const languageLabel = (language?: keyof typeof Language | null): string =>
  language ? (iso639.find(l => l.enumCode === language)?.english ?? language) : '';

export const createCourseManagerConfig = (opts?: CourseManagerConfigOpts): ERMEntityDefinition<typeof LMSCourse> => ({
  _class: LMSCourse,
  label: 'Courses',
  icon: 'icon-[mdi--school-outline]',
  detail: {
    operation: id => getByID(LMSCourse, id) as (_gqlFields: BaseQLSelectionSet<typeof LMSCourse>) => Promise<LMSCourse>,
    selectionSet: {
      _id: 1,
      title: 1,
      description: 1,
      language: 1,
      passingScorePercent: 1,
      maxAttempts: 1,
      createdAt: 1,
      archivedAt: 1,
    },
    title: e => e.title ?? 'New course',
    sections: [
      {
        title: 'Course information',
        fields: [
          { fieldName: 'title', label: 'Title' },
          { fieldName: 'description', label: 'Description' },
          {
            fieldName: 'language',
            label: 'Language',
            render(props) {
              const field = props.form?.field('language');
              if (!field) return nothing;
              return ErmValue(
                'Language',
                'translate',
                DropdownSelectionInput({
                  ...field,
                  selectables: Object.values(Language),
                  renderString: (language: keyof typeof Language) =>
                    iso639.find(l => l.enumCode === language)?.english ?? language,
                  placeholder: 'e.g. English',
                }),
              );
            },
          },
          { fieldName: 'passingScorePercent', label: 'Passing score (%)' },
          { fieldName: 'maxAttempts', label: 'Max attempts' },
        ],
      },
      {
        title: 'Course pages',
        render: ({ entity }) => (entity?._id ? CoursePageEditorSection({ courseId: entity._id }) : nothing),
      },
      {
        title: 'Enrollments',
        render: ({ entity }) =>
          entity?._id
            ? (opts?.enrollmentManagerComponent?.({ courseId: entity._id }) ??
              CourseEnrollmentManager({ courseId: entity._id }))
            : nothing,
      },
    ],
    // The ERM viewer's delete button has no confirmation or post-delete navigation, so we own both:
    // confirm, soft-delete (stamps `deletedAt`), then go back to the list on success.
    onDelete: async entity => {
      const done = await guardDialog(() => softDeleteCourse(entity._id)(), {
        hasToConfirm: true,
        confirmText: `Delete "${entity.title?.trim() || 'course'}"? It will be removed from the list. This cannot be undone.`,
        acceptText: 'Delete',
        declineText: 'Cancel',
        highlightDecline: true,
        successText: 'Course deleted',
        errorMessagePrefix: 'Could not delete course',
      });
      if (done) window.history.back();
    },
  },
  list: {
    operation: subscribeScopedCoursesPaginated,
    selectionSet: {
      _id: 1,
      title: 1,
      language: 1,
      passingScorePercent: 1,
      maxAttempts: 1,
      archivedAt: 1,
      createdAt: 1,
    },
    columns: [
      {
        fieldName: 'title',
        columnName: 'Course',
        isAlwaysVisible: true,
        selectionSet: { title: 1, language: 1 },
        renderItem: course => html`
          <div class="flex flex-col py-2 px-4">
            <span class="font-medium">${course.title ?? 'New course'}</span>
            <span class="text-xs text-neutral-500">${languageLabel(course.language) || 'Unknown'}</span>
          </div>
        `,
      },
      {
        fieldName: 'archivedAt',
        columnName: 'Status',
        selectionSet: { archivedAt: 1 },
        getFieldMapping: course =>
          course.archivedAt ? html`<span class="badge badge-warning badge-sm">Archived</span>` : nothing,
      },
    ],
    searchFields: ['title', 'language'],
    listViews: [countingTableView(filter => countScopedCourses(filter)())],
  },
  route: 'courses',
  onCreate: async () => {
    const course = new LMSCourse({ title: 'New course', passingScorePercent: 90, maxAttempts: 3 });
    await course.create();
    if (course._id) {
      window.location.hash = `/courses/detail/${course._id}`;
    }
    return course._id;
  },
});
